import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Globe,
  ShoppingBag,
  Users,
  Mail,
  ArrowUpRight,
  Instagram,
  Youtube,
  Facebook,
  Check,
  Send,
} from "lucide-react";
import useSEO, { BASE_URL, DEFAULT_OG_IMAGE } from "@/hooks/useSEO";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_barcelona-club-hub/artifacts/9yydi0mf_Logotipo-La-Bombonera-%28Curvas%29.png";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LINKS = [
  {
    to: "/",
    label: "Web oficial",
    caption: "labomboneradebarcelona.es",
    Icon: Globe,
    external: false,
  },
  {
    to: "/tienda",
    label: "Tienda",
    caption: "Camisetas, packs y merch",
    Icon: ShoppingBag,
    external: false,
  },
  {
    to: "/quienes-somos",
    label: "Quiénes somos",
    caption: "El equipo que sostiene el club",
    Icon: Users,
    external: false,
  },
  {
    to: "/contacto",
    label: "Contacto",
    caption: "Hacete socio / escribinos",
    Icon: Mail,
    external: false,
  },
];

const SOCIALS = [
  { href: "https://www.instagram.com/", Icon: Instagram, label: "Instagram" },
  { href: "https://www.youtube.com/", Icon: Youtube, label: "YouTube" },
  { href: "https://www.facebook.com/", Icon: Facebook, label: "Facebook" },
];

export default function Info() {
  useSEO({
    title: "La Bombonera · Enlaces oficiales",
    description:
      "Web oficial, tienda, equipo y contacto del C.D. La Bombonera de Barcelona — todo en un solo lugar.",
    canonical: `${BASE_URL}/info`,
    image: DEFAULT_OG_IMAGE,
  });
  const [form, setForm] = useState({ nombre: "", email: "", mensaje: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setReady(true), 60);
    return () => clearTimeout(t);
  }, []);

  const setField = (k) => (e) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.email || !form.mensaje) {
      toast.error("Completá nombre, email y mensaje.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        source: "Instagram Bio (/info)",
        extra_tag: "lead-from-ig-bio",
      });
      setDone(true);
      toast.success("¡Gracias! Te responderemos pronto.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "No se pudo enviar. Probá de nuevo."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      data-testid="info-page"
      className="relative min-h-screen overflow-hidden pt-28 pb-16"
    >
      {/* Aurora background */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <span className="aurora-blob b1" />
        <span className="aurora-blob b2" />
        <span className="aurora-blob b3" />
        <div className="absolute inset-0 bg-[#030712]/55" />
      </div>

      <div className="relative max-w-[460px] mx-auto px-5">
        {/* Header */}
        <div
          className={`flex flex-col items-center text-center transition-all duration-[900ms] ${
            ready
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-4"
          }`}
        >
          {/* Logo with glow ring */}
          <div className="relative mb-6" data-testid="info-logo">
            <div className="absolute inset-0 rounded-full bg-[#FFB800]/25 blur-xl animate-pulse" />
            <div className="relative w-28 h-28 rounded-full bg-[#030712] border border-[#FFB800]/30 grid place-items-center shadow-2xl">
              <img
                src={LOGO_URL}
                alt="C.D. La Bombonera"
                className="w-20 h-20 object-contain"
              />
            </div>
          </div>

          <p className="text-[10px] uppercase tracking-[0.35em] text-[#FFB800] mb-3">
            Barcelona · 2015
          </p>
          <h1 className="font-display text-4xl font-extrabold uppercase tracking-tight leading-none">
            C.D. La<br />Bombonera
          </h1>
          <p className="text-white/60 mt-4 text-[15px] leading-relaxed max-w-[320px]">
            Elegimos el deporte como estilo de vida. Todo lo que somos, en un
            solo lugar.
          </p>

          {/* Social icons */}
          <div className="flex gap-2.5 mt-6">
            {SOCIALS.map(({ href, Icon, label }, i) => (
              <a
                key={i}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                data-testid={`info-social-${label.toLowerCase()}`}
                className="h-11 w-11 rounded-full grid place-items-center border border-white/15 bg-white/5 backdrop-blur-md text-white/80 hover:text-[#FFB800] hover:border-[#FFB800] hover:bg-[#FFB800]/10 transition-all duration-300"
              >
                <Icon size={16} />
              </a>
            ))}
          </div>
        </div>

        {/* Link stack */}
        <div className="mt-10 flex flex-col gap-3">
          {LINKS.map((l, i) => {
            const Icon = l.Icon;
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`info-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                style={{
                  animationDelay: `${120 + i * 80}ms`,
                  opacity: ready ? 1 : 0,
                  transform: ready ? "translateY(0)" : "translateY(12px)",
                  transition: `opacity 700ms cubic-bezier(.2,.7,.1,1) ${i * 80}ms, transform 700ms cubic-bezier(.2,.7,.1,1) ${i * 80}ms, border-color 300ms, background 300ms`,
                }}
                className="info-link group flex items-center gap-4 rounded-[1.5rem] border border-white/10 bg-white/5 backdrop-blur-xl hover:border-[#FFB800]/60 hover:bg-white/8 px-5 py-4"
              >
                <span className="h-11 w-11 rounded-full bg-[#FFB800] grid place-items-center text-[#030712] flex-shrink-0 transition-transform group-hover:rotate-[-6deg] group-hover:scale-105">
                  <Icon size={18} strokeWidth={2.2} />
                </span>
                <span className="flex-1 flex flex-col text-left">
                  <span className="font-display text-base font-extrabold uppercase tracking-tight text-white">
                    {l.label}
                  </span>
                  <span className="text-[11px] text-white/50 mt-0.5">
                    {l.caption}
                  </span>
                </span>
                <ArrowUpRight
                  size={18}
                  className="text-white/40 group-hover:text-[#FFB800] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all"
                />
              </Link>
            );
          })}
        </div>

        {/* Form */}
        <div
          className="mt-10 rounded-[1.75rem] border border-white/10 bg-white/5 backdrop-blur-xl p-6"
          data-testid="info-form-card"
        >
          {done ? (
            <div className="text-center py-6" data-testid="info-form-success">
              <div className="mx-auto h-12 w-12 rounded-full bg-[#FFB800] grid place-items-center text-[#030712] mb-4">
                <Check size={20} strokeWidth={3} />
              </div>
              <p className="font-display text-xl font-extrabold uppercase tracking-tight">
                ¡Listo!
              </p>
              <p className="text-white/60 text-sm mt-2">
                Te respondemos en las próximas horas.
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-8 bg-[#FFB800]" />
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#FFB800]">
                  Dejanos tu mensaje
                </p>
              </div>
              <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight leading-tight mb-5">
                ¿En qué te<br />podemos ayudar?
              </h3>
              <form onSubmit={onSubmit} className="flex flex-col gap-3" data-testid="info-form">
                <input
                  type="text"
                  value={form.nombre}
                  onChange={setField("nombre")}
                  placeholder="Tu nombre"
                  data-testid="info-input-nombre"
                  className="rounded-full bg-white/5 border border-white/10 text-white placeholder:text-white/30 px-5 py-3 text-[14px] focus:outline-none focus:border-[#FFB800] transition-colors"
                  required
                />
                <input
                  type="email"
                  value={form.email}
                  onChange={setField("email")}
                  placeholder="Tu email"
                  data-testid="info-input-email"
                  className="rounded-full bg-white/5 border border-white/10 text-white placeholder:text-white/30 px-5 py-3 text-[14px] focus:outline-none focus:border-[#FFB800] transition-colors"
                  required
                />
                <textarea
                  value={form.mensaje}
                  onChange={setField("mensaje")}
                  placeholder="Contanos brevemente"
                  rows={3}
                  data-testid="info-input-mensaje"
                  className="rounded-[1.25rem] bg-white/5 border border-white/10 text-white placeholder:text-white/30 px-5 py-3 text-[14px] resize-none focus:outline-none focus:border-[#FFB800] transition-colors"
                  required
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="info-submit"
                  className="mt-2 rounded-full bg-[#FFB800] hover:bg-white text-[#030712] font-bold uppercase tracking-[0.18em] text-[12px] py-3.5 flex items-center justify-center gap-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? "Enviando..." : "Enviar mensaje"}
                  {loading ? <Send size={14} /> : <ArrowUpRight size={14} />}
                </button>
              </form>
            </>
          )}
        </div>

        {/* Footer sig */}
        <p className="text-center text-[10px] uppercase tracking-[0.3em] text-white/30 mt-10 font-mono">
          labomboneradebarcelona.es · EST 2015
        </p>
      </div>
    </main>
  );
}
